// ─── Fil d'une discussion ─────────────────────────────────────────
// Écoute, rendu des bulles, saisie. Les actions sur un message (modérer,
// signaler, supprimer) sont proposées par `actionsMessage` — on ne recalcule
// aucun droit ici.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, FlatList, StyleSheet,
  KeyboardAvoidingView, Platform,
} from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from '../auth/AuthContext';
import { confirmerChoix, confirmerSuppression } from '../services/confirm';
import { usePeutUtiliser } from '../forfaits/FonctionsContext';
import {
  choisirImage, choisirDocument, positionCourante, televerser,
  imagePickerDispo, documentPickerDispo, positionDispo,
} from './piecesJointes';
import {
  ecouterMessagesDiscussion, envoyerMessage, supprimerMessage,
  modererParAuteur, modererParAdmin, signalerMessage, leverSignalement,
} from '../services/messagerie';
import { majLecture } from '../services/messagerie';
import Bulle from './Bulle';

export default function FilMessages({ disc }) {
  // `disc.cle` vient du CHEMIN d'ecoute, pas du document : c'est elle qui situe
  // la discussion sous son porteur (cf. cheminDiscussion.js).
  const cle = disc?.cle;
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const uid = user?.uid || null;
  const s = useMemo(() => styles(UI), [UI]);
  const [messages, setMessages] = useState([]);
  const [saisie, setSaisie] = useState('');
  const [envoiPJ, setEnvoiPJ] = useState(false);
  // Porte de forfait. La clé est `messagerie.pieceJointe` — une clé ABSENTE du
  // catalogue échoue EN OUVERT (`forfaits/fonctionnalites.js`), donc un écart
  // de nom offrirait la fonction payante à tous, en silence.
  const peut = usePeutUtiliser();
  const peutPJ = peut('messagerie.pieceJointe');
  const liste = useRef(null);

  useEffect(() => {
    if (!disc?.id) { setMessages([]); return undefined; }
    return ecouterMessagesDiscussion(cle, disc.id, setMessages);
  }, [disc?.id]);

  // Marquer lu à l'ouverture ET à chaque arrivée : « lu » sur un appareil vaut
  // partout, le badge ne doit pas remonter sur un fil qu'on a sous les yeux.
  useEffect(() => {
    if (uid && disc?.id) majLecture(uid, disc.id).catch(() => {});
  }, [uid, disc?.id, messages.length]);

  const envoyer = async () => {
    const texte = saisie.trim();
    if (!texte || disc?.ferme) return;
    setSaisie('');                       // vidé AVANT l'aller-retour réseau
    try {
      await envoyerMessage({
        cle, discussionId: disc.id, auteurUid: uid,
        auteurNom: user?.prenom || user?.email || '', texte,
      });
    } catch { setSaisie(texte); }        // rendu à l'utilisateur si ça échoue
  };

  const envoyerPJ = async (type, source) => {
    if (envoiPJ || disc?.ferme) return;
    setEnvoiPJ(true);
    try {
      const media = type === 'position' ? null : await televerser(source.uri, disc.id, { nom: source.nom });
      await envoyerMessage({
        cle, discussionId: disc.id, auteurUid: uid,
        auteurNom: user?.prenom || user?.email || '',
        texte: '', type, media, position: type === 'position' ? source : null,
      });
    } catch { /* l'upload ou l'envoi a échoué : rien n'est posté */ }
    setEnvoiPJ(false);
  };

  // Le menu ne propose que ce qui est RÉELLEMENT disponible : un module Expo
  // absent (app qui ne l'embarque pas) ne doit pas offrir une action morte.
  const ouvrirPieceJointe = () => {
    const options = [];
    if (imagePickerDispo()) {
      options.push({ text: t('messagerie.photo'), onPress: async () => {
        const a = await choisirImage(); if (a) envoyerPJ('image', a);
      } });
      options.push({ text: t('messagerie.camera'), onPress: async () => {
        const a = await choisirImage({ camera: true }); if (a) envoyerPJ('image', a);
      } });
    }
    if (documentPickerDispo()) {
      options.push({ text: t('messagerie.document'), onPress: async () => {
        const a = await choisirDocument(); if (a) envoyerPJ('fichier', a);
      } });
    }
    if (positionDispo()) {
      options.push({ text: t('messagerie.partagerPosition'), onPress: async () => {
        const p = await positionCourante(); if (p) envoyerPJ('position', p);
      } });
    }
    if (!options.length) return;
    confirmerChoix(t('messagerie.piecesJointes'), '', options);
  };

  // Un appui long ouvre les actions RÉELLEMENT disponibles pour ce lecteur.
  const ouvrirActions = (msg, a) => {
    const options = [];
    if (a.supprimer) options.push({ cle: 'supprimer', label: t('commun.supprimer'), destructif: true });
    if (a.modererAuteur) options.push({ cle: 'modererAuteur', label: t('messagerie.retirerPourTous') });
    if (a.modererAdmin) options.push({ cle: 'modererAdmin', label: t('messagerie.modererMasquer') });
    if (a.signaler) options.push({ cle: 'signaler', label: t('messagerie.signaler') });
    if (a.leverSignalement) options.push({ cle: 'lever', label: t('messagerie.leverSignalement') });
    if (!options.length) return;

    confirmerChoix(
      t('messagerie.actionsMessage'), '',
      options.map(o => ({
        text: o.label,                   // `text`, pas `texte` : signature de confirmerChoix
        style: o.destructif ? 'destructive' : 'default',
        onPress: () => executer(o.cle, msg),
      })),
    );
  };

  // Le parametre s'appelle `action` et NON `cle` : `cle` designe deja le
  // porteur de la discussion dans ce composant, et le masquer ferait passer
  // « supprimer » a la place du calendrier. Bug invisible a la compilation.
  const executer = (action, msg) => {
    const nom = user?.prenom || user?.email || '';
    // Les deux gestes SANS RETOUR passent par une confirmation : ni la
    // suppression ni la modération ne se défont.
    if (action === 'supprimer') {
      return confirmerSuppression(t('commun.supprimer'), t('messagerie.confirmSupprimer'),
        () => supprimerMessage(cle, disc.id, msg.id).catch(() => {}));
    }
    if (action === 'modererAuteur') {
      return confirmerSuppression(t('messagerie.retirerPourTous'), t('messagerie.confirmRetirer'),
        () => modererParAuteur(cle, disc.id, msg.id, uid, nom).catch(() => {}));
    }
    if (action === 'modererAdmin') {
      return confirmerSuppression(t('messagerie.modererMasquer'), t('messagerie.confirmModerer'),
        () => modererParAdmin(cle, disc.id, msg.id, uid, nom).catch(() => {}));
    }
    if (action === 'signaler') return signalerMessage(cle, disc.id, msg.id, { uid, nom }).catch(() => {});
    if (action === 'lever') return leverSignalement(cle, disc.id, msg.id, uid).catch(() => {});
    return undefined;
  };

  return (
    <KeyboardAvoidingView
      style={s.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={80}
    >
      <FlatList
        ref={liste}
        data={messages}
        keyExtractor={(m) => m.id}
        renderItem={({ item }) => (
          <Bulle msg={item} uid={uid} disc={disc} onAction={ouvrirActions} />
        )}
        contentContainerStyle={s.contenu}
        onContentSizeChange={() => liste.current?.scrollToEnd({ animated: false })}
        ListEmptyComponent={(
          <View style={s.vide}>
            <Text style={s.videTitre}>{t('messagerie.aucunMessage')}</Text>
            <Text style={s.videTxt}>{t('messagerie.demarrezConversation')}</Text>
          </View>
        )}
      />

      {disc?.ferme ? (
        <Text style={s.fermee}>{t('messagerie.discussionFermee')}</Text>
      ) : (
        <View style={s.barre}>
          {peutPJ && (
            <TouchableOpacity onPress={ouvrirPieceJointe} disabled={envoiPJ} style={s.pj}>
              <Text style={[s.pjTxt, envoiPJ && { opacity: 0.35 }]}>📎</Text>
            </TouchableOpacity>
          )}
          <TextInput
            style={s.champ}
            value={saisie}
            onChangeText={setSaisie}
            placeholder={t('messagerie.votreMessage')}
            placeholderTextColor={UI.faint}
            multiline
          />
          <TouchableOpacity onPress={envoyer} disabled={!saisie.trim()} style={s.envoi}>
            <Text style={[s.envoiTxt, !saisie.trim() && { opacity: 0.35 }]}>➤</Text>
          </TouchableOpacity>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = (UI) => StyleSheet.create({
  flex: { flex: 1 },
  contenu: { paddingVertical: 10, flexGrow: 1 },
  vide: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  videTitre: { fontSize: 15, fontWeight: '800', color: UI.muted },
  videTxt: { fontSize: 13, color: UI.faint, marginTop: 4 },
  barre: {
    flexDirection: 'row', alignItems: 'flex-end', gap: 8,
    padding: 8, borderTopWidth: 1, borderTopColor: UI.border, backgroundColor: UI.card,
  },
  champ: {
    flex: 1, maxHeight: 120, backgroundColor: UI.champ, borderRadius: 18,
    paddingHorizontal: 14, paddingVertical: 9, fontSize: 15, color: UI.ink,
    borderWidth: 0.5, borderColor: UI.champBord,
  },
  envoi: { paddingHorizontal: 10, paddingVertical: 8 },
  pj: { paddingHorizontal: 6, paddingVertical: 8 },
  pjTxt: { fontSize: 19 },
  envoiTxt: { fontSize: 20, color: UI.accent },
  fermee: {
    textAlign: 'center', padding: 14, fontSize: 13, color: UI.muted,
    fontStyle: 'italic', borderTopWidth: 1, borderTopColor: UI.border,
  },
});
